import { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { LocalizedLink } from '@/components/LocalizedLink'
import { SignupFormData } from './types'
import { SignupFormErrors, validateForm } from './validation'
import { StandardLoader } from '../StandardLoader'
import { PasswordInput } from '../PasswordInput'
import { useAuth } from '@/hooks/useAuth'
import { useTar, TypedTrans } from '@/modules'
import { Button } from '@duck/ui'
import { generateSlug } from '@/utils/slugger-client'

interface SignupFormProps {
  onStepChange?: (step: 1 | 2) => void
}

export const SignupForm = ({ onStepChange }: SignupFormProps) => {
  const [formErrors, setFormErrors] = useState<SignupFormErrors>({})
  const { register, isRegistering, invitationData, invitationLoading, assignmentToken } = useAuth()
  const { t } = useTar()

  // Step state: 1 = email, 2 = rest of form
  // If invitation has email (non-public-assignment), start at step 2
  const [step, setStep] = useState<1 | 2>(
    invitationData?.email && !invitationData.isPublicAssignment ? 2 : 1
  )
  const [email, setEmail] = useState<string>(invitationData?.email || '')
  const [emailPlaceholder] = useState(() => generateSlug())
  useEffect(() => {
    if (invitationData?.email) {
      setEmail(invitationData.email)
    }
  }, [invitationData?.email])


  // Use the specific loading state for registration
  const isLoading = isRegistering || invitationLoading

  const handleStep1Submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isLoading) return

    // Validate email only
    const errors = validateForm({ email })
    const emailError = errors.email ? { email: errors.email } : null
    if (emailError) {
      setFormErrors(emailError)
    } else {
      setFormErrors({})
      setStep(2)
      onStepChange?.(2)
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isLoading) return

    const formData = new FormData(e.currentTarget)
    const signupData: SignupFormData = {
      email: email,
      firstName: formData.get('firstName') as string,
      lastName: formData.get('lastName') as string,
      password: formData.get('password') as string,
      confirmPassword: formData.get('confirmPassword') as string,
      terms: formData.get('terms') === 'on',
      ...(assignmentToken ? { assignmentToken } : {}),
    }

    const errors = validateForm(signupData)
    setFormErrors(errors)

    if (Object.keys(errors).length === 0) {
      await register(signupData)
    }
  }

  // Step 1: Email only
  if (step === 1) {
    const disableEmail = isLoading || invitationLoading || (!!invitationData && !invitationData.isPublicAssignment)
    return (
      <form onSubmit={handleStep1Submit} className="space-y-4" autoComplete="on">
        {invitationLoading && (
          <div className="flex items-center justify-center p-4 bg-accent/10 rounded-lg">
            <StandardLoader size="sm" />
            <span className="ms-2 text-sm text-muted-foreground">{t('common.loadingInvitation')}</span>
          </div>
        )}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            {t('auth.emailAddress')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            disabled={disableEmail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground
                     placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                     focus:border-accent transition-colors
                     ${formErrors.email ? 'border-error' : 'border-border'}
                     ${disableEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
            placeholder={emailPlaceholder}
          />
          {formErrors.email && (
            <p className="text-error text-xs mt-1">{formErrors.email}</p>
          )}
        </div>

        <Button
          type="submit"
          // variant="outline"
          disabled={isLoading || invitationLoading}
          className={`w-full px-4 py-2 rounded-lg font-medium
                   transition-colors duration-200 flex items-center justify-center hover:text-white`}
        >
          {isLoading || invitationLoading ? (
            <>
              <StandardLoader size="sm" />
              <span className="ms-2">{t('common.loading')}</span>
            </>
          ) : (
            t('common.continueWithEmail')
          )}
        </Button>
      </form>
    )
  }
  // End Step 1
  // Step 2: First name, last name, passwords, and agree checkbox
  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on" key={invitationData?.email ?? 'no-invite'}>
      {invitationLoading && (
        <div className="flex items-center justify-center p-4 bg-accent/10 rounded-lg">
          <StandardLoader size="sm" />
          <span className="ms-2 text-sm text-muted-foreground">{t('common.loadingInvitation')}</span>
        </div>
      )}

      {/* Email display (read-only in step 2) */}
      <div>
        <label htmlFor="email-display" className="block text-sm font-medium text-foreground mb-2">
          {t('auth.emailAddress')}
        </label>
        <input
          type="email"
          id="email-display"
          value={email}
          disabled
          className="w-full px-3 py-2 border rounded-lg bg-background text-foreground
                   opacity-50 cursor-not-allowed border-border"
        />
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => {
            setStep(1)
            setFormErrors({})
            onStepChange?.(1)
          }}
          className="text-xs text-accent hover:text-foreground mt-1 p-0 h-auto"
        >
          {t('common.changeEmail')}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
            {t('common.firstName')}
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            autoComplete="given-name"
            disabled={isLoading || invitationLoading}
            defaultValue={invitationData?.firstName || ''}
            className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground
                     placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                     focus:border-accent transition-colors
                     ${formErrors.firstName ? 'border-error' : 'border-border'}
                     ${isLoading || invitationLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            placeholder={t('auth.firstNamePlaceholder')}
          />
          {formErrors.firstName && (
            <p className="text-error text-xs mt-1">{formErrors.firstName}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
            {t('common.lastName')}
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            autoComplete="family-name"
            disabled={isLoading || invitationLoading}
            defaultValue={invitationData?.lastName || ''}
            className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground
                     placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                     focus:border-accent transition-colors
                     ${formErrors.lastName ? 'border-error' : 'border-border'}
                     ${isLoading || invitationLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            placeholder={t('auth.lastNamePlaceholder')}
          />
          {formErrors.lastName && (
            <p className="text-error text-xs mt-1">{formErrors.lastName}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
          {t('common.password')}
        </label>
        <PasswordInput
          id="password"
          name="password"
          autoComplete="new-password"
          disabled={isLoading || invitationLoading}
          hasError={!!formErrors.password}
          placeholder={t('auth.passwordPlaceholderSignup')}
        />
        {formErrors.password && (
          <p className="text-error text-xs mt-1">{formErrors.password}</p>
        )}
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
          {t('auth.confirmPassword')}
        </label>
        <PasswordInput
          id="confirmPassword"
          name="confirmPassword"
          autoComplete="new-password"
          disabled={isLoading || invitationLoading}
          hasError={!!formErrors.confirmPassword}
          placeholder={t('auth.confirmPasswordPlaceholder')}
        />
        {formErrors.confirmPassword && (
          <p className="text-error text-xs mt-1">{formErrors.confirmPassword}</p>
        )}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="terms"
          name="terms"
          disabled={isLoading || invitationLoading}
          className="h-4 w-4 text-accent focus:ring-ring border-border rounded"
        />
        <label htmlFor="terms" className="ms-2 text-sm text-muted-foreground">
          <TypedTrans
            i18nKey="common.iAgreeToTheXandY"
            components={[
              <LocalizedLink
                key="terms"
                to="/terms-of-service"
                className="text-accent hover:text-foreground transition-colors"
                target="_blank"
              />,
              <LocalizedLink
                key="privacy"
                to="/privacy-policy"
                className="text-accent hover:text-foreground transition-colors"
                target="_blank"
              />,
            ]}
          />
        </label>
      </div>
      {formErrors.terms && (
        <p className="text-error text-xs mt-1">{formErrors.terms}</p>
      )}

      <Button
        type="submit"
        data-testid="signup-submit"
        disabled={isLoading}
        className={`w-full px-4 py-2 rounded-lg font-medium
                 transition-colors duration-200 flex items-center justify-center
                 ${isLoading
            ? 'bg-accent/50 text-white cursor-not-allowed opacity-75'
            : 'bg-accent text-white hover:bg-accent/90'
          }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <StandardLoader size="sm" />
            <span>{t('common.loading')}</span>
          </div>
        ) : (
          t('common.signup')
        )}
      </Button>
    </form>
  )
} 